import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import MedicamentAutocomplete from '@/components/feature/MedicamentAutocomplete';
import {
  VOIES_GROUPES,
  getVoie,
  getDefaultConditionnement,
  formatLigneOrdonnance,
} from '@/lib/voiesMedicament';
import type { MedicamentLigne } from '@/lib/voiesMedicament';

export interface OrdonnanceSortieData {
  diagnostic: string;
  medicaments: MedicamentLigne[];
  duree: string;
  instructions: string;
}

interface OrdonnanceSortieSectionProps {
  patientId: string;
  patientName: string;
  medecinId: string;
  suiteId: string;
  onCreated: (data: OrdonnanceSortieData) => void;
}

type MedicamentField = 'nom' | 'dosageNb' | 'nbBoites' | 'conditionnement' | 'forme' | 'instructions' | 'duree' | 'voieInjection' | 'siteApplication' | 'siteGouttes' | 'dissolution';

const inputClass = 'w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none transition-colors text-sm bg-white';
const textareaClass = 'w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none transition-colors resize-none text-sm bg-white';
const labelClass = 'block text-sm font-semibold text-gray-700 mb-2';

const medInputClass = 'w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-clinic-blue focus:outline-none transition-colors text-sm bg-white';
const medSelectClass = 'px-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm bg-white focus:border-clinic-blue focus:outline-none cursor-pointer';

const emptyLigne = (): MedicamentLigne => ({
  id: Date.now().toString(),
  nom: '',
  dosageNb: '1',
  forme: 'comprime',
  matin: true,
  midi: false,
  soir: false,
  nbBoites: '1',
  conditionnement: getDefaultConditionnement('comprime'),
  instructions: '',
  duree: '',
});

interface OrdonnanceDraft {
  diagnostic: string;
  medicaments: MedicamentLigne[];
}

export default function OrdonnanceSortieSection({
  patientId,
  patientName,
  medecinId,
  suiteId,
  onCreated,
}: OrdonnanceSortieSectionProps) {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [draft, setDraft] = useState<OrdonnanceDraft>({ diagnostic: '', medicaments: [emptyLigne()] });

  const diagnostic = draft.diagnostic;
  const medicaments = draft.medicaments;

  const setDiagnostic = (v: string) => setDraft((prev) => ({ ...prev, diagnostic: v }));
  const setMedicaments = (updater: MedicamentLigne[] | ((prev: MedicamentLigne[]) => MedicamentLigne[])) =>
    setDraft((prev) => ({
      ...prev,
      medicaments: typeof updater === 'function'
        ? (updater as (p: MedicamentLigne[]) => MedicamentLigne[])(prev.medicaments)
        : updater,
    }));

  const addMedicament = () =>
    setMedicaments((prev) => [...prev, emptyLigne()]);
  const updateMedicament = (id: string, field: MedicamentField, v: string) =>
    setMedicaments((prev) => prev.map((m) => {
      if (m.id !== id) return m;
      const updated = { ...m, [field]: v };
      // Quand la forme change, recalculer le conditionnement par défaut
      if (field === 'forme') {
        updated.conditionnement = getDefaultConditionnement(v);
      }
      return updated;
    }));
  const toggleMoment = (id: string, moment: 'matin' | 'midi' | 'soir') =>
    setMedicaments((prev) => prev.map((m) => (m.id === id ? { ...m, [moment]: !m[moment] } : m)));
  const removeMedicament = (id: string) => {
    if (medicaments.length <= 1) return;
    setMedicaments((prev) => prev.filter((m) => m.id !== id));
  };

  const handleSubmit = async () => {
    const valides = medicaments.filter((m) => m.nom.trim());
    if (valides.length === 0) {
      setErrorMsg('Ajoutez au moins un médicament à l\'ordonnance de sortie.');
      return;
    }
    setLoading(true);
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      const medicamentsNoms = valides.map((m) => formatLigneOrdonnance(m));
      const instructionsCombinees = valides
        .map((m) => m.instructions.trim())
        .filter(Boolean)
        .join('\n');
      const dureesCombinees = valides
        .map((m) => m.duree.trim())
        .filter(Boolean)
        .join(' ; ');

      const { error } = await supabase.from('prescriptions').insert({
        patient_id: patientId,
        medecin_id: medecinId || null,
        suite_soin_id: suiteId,
        type_ordonnance: 'sortie',
        diagnostic: diagnostic.trim() || null,
        medicaments: medicamentsNoms,
        duree_traitement: dureesCombinees || null,
        instructions: instructionsCombinees || null,
        date_prescription: new Date().toISOString(),
      });
      if (error) throw error;

      onCreated({
        diagnostic: diagnostic.trim(),
        medicaments: valides,
        duree: dureesCombinees,
        instructions: instructionsCombinees,
      });

      setSuccessMsg('Ordonnance de sortie enregistrée. Vous pouvez l\'imprimer.');
      setTimeout(() => setSuccessMsg(null), 4000);

      // Reset du formulaire
      setDiagnostic('');
      setMedicaments([emptyLigne()]);
    } catch (err: any) {
      setErrorMsg(err.message || 'Erreur lors de la création de l\'ordonnance.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-amber-200 overflow-hidden">
      <div className="p-6 border-b border-amber-100 bg-amber-50/40">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        Ordonnance de sortie
          </h2>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Patient : <strong>{patientName}</strong>
        </p>
      </div>

      <div className="p-6 space-y-5">
        {errorMsg && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
            <i className="ri-error-warning-line flex-shrink-0"></i>{errorMsg}
          </div>
        )}

        {successMsg && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700 flex items-center gap-2">
            <i className="ri-checkbox-circle-line flex-shrink-0"></i>{successMsg}
          </div>
        )}

        <div>
          <label className={labelClass}>Diagnostic de sortie</label>
          <textarea value={diagnostic} onChange={(e) => setDiagnostic(e.target.value)} className={textareaClass} rows={2} maxLength={500} placeholder="Diagnostic au moment de la sortie (optionnel)..." />
        </div>

        <div>
          <h3 className="text-md font-bold text-gray-800 mb-4 flex items-center gap-2">
                        Ordonnance
          </h3>

          <div className="space-y-3">
            {medicaments.map((m, i) => (
              <div key={m.id} className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                <div className="flex gap-2 items-start">
                  <div className="flex-1">
                    <MedicamentAutocomplete
                      value={m.nom}
                      onChange={(nom) => updateMedicament(m.id, 'nom', nom)}
                      placeholder={`Médicament ${i + 1}`}
                      className={medInputClass}
                    />
                  </div>
                  {medicaments.length > 1 && (
                    <button onClick={() => removeMedicament(m.id)} type="button" className="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer flex-shrink-0 mt-1">
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {/* Voie / forme */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 font-medium">Voie / Forme</span>
                    <select
                      value={m.forme}
                      onChange={(e) => updateMedicament(m.id, 'forme', e.target.value)}
                      className={medSelectClass}
                    >
                      {VOIES_GROUPES.map((g) => (
                        <optgroup key={g.groupe} label={g.groupe}>
                          {g.voies.map((v) => (
                            <option key={v.key} value={v.key}>{v.label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  {/* Dosage / quantité — adapté à la voie */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 font-medium">{getVoie(m.forme).dosageLabel}</span>
                    {getVoie(m.forme).dosageType === 'count' ? (
                      <select
                        value={m.dosageNb}
                        onChange={(e) => updateMedicament(m.id, 'dosageNb', e.target.value)}
                        className={medSelectClass}
                      >
                        {['1', '2', '3', '4', '5', '6'].map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={m.dosageNb}
                        onChange={(e) => updateMedicament(m.id, 'dosageNb', e.target.value)}
                        className="w-28 px-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm bg-white focus:border-clinic-blue focus:outline-none"
                        placeholder={getVoie(m.forme).dosagePlaceholder || 'ex: 1'}
                      />
                    )}
                  </div>
                  {/* Moments */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-500 font-medium mr-1">×</span>
                    <label className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                      m.matin ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={m.matin}
                        onChange={() => toggleMoment(m.id, 'matin')}
                        className="hidden"
                      />
                      Matin
                    </label>
                    <label className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                      m.midi ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={m.midi}
                        onChange={() => toggleMoment(m.id, 'midi')}
                        className="hidden"
                      />
                      Midi
                    </label>
                    <label className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                      m.soir ? 'bg-sky-100 text-sky-800 border border-sky-300' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={m.soir}
                        onChange={() => toggleMoment(m.id, 'soir')}
                        className="hidden"
                      />
                      Soir
                    </label>
                  </div>
                  {/* Conditionnement (boîtes / plaquettes) */}
                  {getVoie(m.forme).showBoites && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 font-medium">Conditionnement</span>
                      <select
                        value={m.nbBoites}
                        onChange={(e) => updateMedicament(m.id, 'nbBoites', e.target.value)}
                        className={medSelectClass}
                      >
                        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      <select
                        value={m.conditionnement || 'boite'}
                        onChange={(e) => updateMedicament(m.id, 'conditionnement', e.target.value)}
                        className={medSelectClass}
                      >
                        <option value="boite">boîte(s)</option>
                        <option value="plaquette">plaquette(s)</option>
                      </select>
                    </div>
                  )}
                  {getVoie(m.forme).showVoieInjection && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 font-medium">Voie</span>
                      <select
                        value={m.voieInjection || ''}
                        onChange={(e) => updateMedicament(m.id, 'voieInjection', e.target.value)}
                        className={medSelectClass}
                      >
                        <option value="">Choisir...</option>
                        <option value="IV">IV (intraveineuse)</option>
                        <option value="IM">IM (intramusculaire)</option>
                        <option value="SC">SC (sous-cutanée)</option>
                        <option value="ID">ID (intradermique)</option>
                      </select>
                    </div>
                  )}
                  {getVoie(m.forme).showSiteGouttes && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 font-medium">Localisation</span>
                      <select
                        value={m.siteGouttes || ''}
                        onChange={(e) => updateMedicament(m.id, 'siteGouttes', e.target.value)}
                        className={medSelectClass}
                      >
                        <option value="">Choisir...</option>
                        <option value="yeux">Yeux</option>
                        <option value="oreilles">Oreilles</option>
                        <option value="nez">Nez</option>
                      </select>
                    </div>
                  )}
                  {getVoie(m.forme).showDissolution && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 font-medium">Préparation</span>
                      <select
                        value={m.dissolution || ''}
                        onChange={(e) => updateMedicament(m.id, 'dissolution', e.target.value)}
                        className={medSelectClass}
                      >
                        <option value="">Choisir...</option>
                        <option value="à dissoudre">À dissoudre</option>
                        <option value="à prendre directement">À prendre directement</option>
                      </select>
                    </div>
                  )}
                </div>
                {getVoie(m.forme).showSiteApplication && (
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">Sur quelle zone / partie du corps ?</label>
                    <input
                      type="text"
                      value={m.siteApplication || ''}
                      onChange={(e) => updateMedicament(m.id, 'siteApplication', e.target.value)}
                      className={medInputClass}
                      placeholder="Ex: sur la plaie, sur le bras, sur le visage..."
                    />
                  </div>
                )}
                {/* Durée + Instructions liées à ce médicament */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">Durée du traitement</label>
                    <input
                      type="text"
                      value={m.duree}
                      onChange={(e) => updateMedicament(m.id, 'duree', e.target.value)}
                      className={medInputClass}
                      placeholder="Ex: 7 jours"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">Instructions</label>
                    <input
                      type="text"
                      value={m.instructions}
                      onChange={(e) => updateMedicament(m.id, 'instructions', e.target.value)}
                      className={medInputClass}
                      placeholder="Ex: pendant les repas, au coucher..."
                    />
                  </div>
                </div>
                {/* Résumé posologie */}
                <div className="text-xs text-gray-400 italic">
                  → {formatLigneOrdonnance(m)}
                </div>
              </div>
            ))}
          </div>
          <button onClick={addMedicament} type="button" className="mt-3 px-3 py-1.5 bg-clinic-blue/10 text-clinic-blue rounded-lg hover:bg-clinic-blue/15 text-xs font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1">
            <i className="ri-add-line"></i>Ajouter un médicament
          </button>
        </div>

        <div className="flex justify-end pt-2">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-5 py-2.5 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm flex items-center justify-center gap-2"
          >
            {loading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
            Enregistrer & imprimer
          </button>
        </div>
      </div>
    </section>
  );
}